// client/src/hooks/useDocuments.js

import { useState, useCallback, useRef } from 'react';
import documentService from '../services/documentService';

const useDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalPages: 1,
    totalItems: 0
  });
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');
  // Filtres qui changent vite (recherche, tag, dates, page) : sans ce garde, une
  // réponse obsolète peut résoudre après une plus récente et écraser la liste.
  const latestListRequest = useRef(0);

  const withLoading = useCallback(async (key, fn) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    setError('');
    try {
      return await fn();
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  const fetchDocuments = useCallback(async (filters, page = 1, limit = 50) => {
    const requestId = ++latestListRequest.current;
    setLoading((prev) => ({ ...prev, list: true }));
    setError('');
    try {
      const { data, pagination: p } = await documentService.getDocuments(filters, page, limit);
      if (requestId !== latestListRequest.current) return data;
      setDocuments(data);
      setPagination(p);
      return data;
    } catch (err) {
      if (requestId === latestListRequest.current) {
        setError(err.message || 'Une erreur est survenue.');
      }
      throw err;
    } finally {
      if (requestId === latestListRequest.current) {
        setLoading((prev) => ({ ...prev, list: false }));
      }
    }
  }, []);

  const uploadDocument = useCallback(
    (payload) =>
      withLoading('upload', async () => {
        const doc = await documentService.uploadDocument(payload);
        setDocuments((prev) => [doc, ...prev]);
        return doc;
      }),
    [withLoading]
  );

  const updateDocument = useCallback(
    (id, data) =>
      withLoading('update', async () => {
        const doc = await documentService.updateDocument(id, data);
        setDocuments((prev) => prev.map((d) => (d.id === id ? doc : d)));
        return doc;
      }),
    [withLoading]
  );

  const deleteDocument = useCallback(
    (id) =>
      withLoading('delete', async () => {
        await documentService.deleteDocument(id);
        setDocuments((prev) => prev.filter((d) => d.id !== id));
      }),
    [withLoading]
  );

  const viewDocument = useCallback(
    (id) => withLoading('view', () => documentService.fetchFileBlobUrl(id)),
    [withLoading]
  );

  return {
    documents,
    pagination,
    loading,
    error,
    setError,
    fetchDocuments,
    uploadDocument,
    updateDocument,
    deleteDocument,
    viewDocument
  };
};

export default useDocuments;
